import React, { ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { useSentryInit } from "./hooks/useSentryInit";
import { useSentryUserSession } from "./hooks/useSentryUserSession";

interface SentryProviderProps {
  children: ReactNode;
}

interface ErrorFallbackProps {
  error: unknown;
  componentStack: string;
  resetError: () => void;
}

function ErrorFallback({ error, componentStack, resetError }: ErrorFallbackProps) {
  const message = error instanceof Error ? error.message : String(error);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white shadow-lg rounded-2xl p-6">
        <h2 className="text-2xl font-semibold text-red-600 mb-4">Oops! Something went wrong.</h2>
        <p className="text-gray-700 mb-2">{message}</p>
        <details className="text-sm text-gray-500 mb-4 whitespace-pre-wrap">
          {componentStack}
        </details>
        <button
          onClick={resetError}
          className="mt-2 w-full inline-flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          Try Again
        </button>
      </div>
    </div>
  );
}

export function SentryProvider({ children }: SentryProviderProps) {
  useSentryInit();
  useSentryUserSession();

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, componentStack, resetError }) => (
        <ErrorFallback error={error} componentStack={componentStack} resetError={resetError} />
      )}>
      <Sentry.Profiler>{children}</Sentry.Profiler>
    </Sentry.ErrorBoundary>
  );
}
